/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */
import { Box, Button, DialogContent, IconButton, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Entry, ENTRY_REVIEW_STATE } from '../../../../../../types/course/Entry';
import CancelIcon from '@mui/icons-material/Cancel';
import React from 'react';
import { observer } from 'mobx-react';
import { useViewModel } from '../../../../../../hooks/useViewModel';
import { EntryContentButtonBoxView } from './ButtonBox/EntryContentButtonBoxView';
import { EntryContentViewModel } from './EntryContentViewModel';
import EntryContentViewStyles from './EntryContentViewStyles';
import { EntryContentDataView } from './EntryContentData/EntryContentDataView';
import theme from '../../../../../../theme';

interface Props {
	entry: Entry;
	isMapView?: boolean;
}

export const EntryContentView = observer((props: Props) => {
	const { classes } = EntryContentViewStyles();
	const viewModel: EntryContentViewModel = useViewModel(EntryContentViewModel);
	const { entry, isMapView } = props;

	const dateString = viewModel.getDateString(entry.date);
	const imageUrl = viewModel.getStudentImgUrl(entry.studentImgUrl);

	return (
		<>
			<Box className={classes.header}>
				<Box className={classes.titleContainer}>
					<Typography variant='h1' className={classes.title}>
						{viewModel.isPublishingView() ? (
							<FormattedMessage id={'entries.publish_title'} />
						) : (
							<FormattedMessage id={'entries.title'} />
						)}
						{dateString}
					</Typography>
					<Typography variant='subtitle1' className={classes.subTitle}>
						<FormattedMessage id={'entries.type.shared_as'} />
						<FormattedMessage id={'entries.type.' + entry.type} />
					</Typography>
				</Box>
				{!isMapView && entry.state === ENTRY_REVIEW_STATE.APPROVED && !viewModel.isPublishingView() && (
					<Button
						variant='outlined'
						size='medium'
						className={`${classes.publishButton} ${classes.buttonLabel}`}
						onClick={() => {
							viewModel.setIsPublishingView(true);
						}}>
						<FormattedMessage id={'entries.publish'} />
					</Button>
				)}
				<IconButton className={classes.cancelButton} onClick={() => viewModel.closeDetailView()}>
					<CancelIcon />
				</IconButton>
			</Box>
			<Box className={classes.headerInfoContainer}>
				<img src={imageUrl} className={classes.studentImage} />
				<Box className={classes.headerInfoText}>
					<Typography variant='h3'>{entry.studentName}</Typography>
					<Typography variant='body2' className={classes.courseName}>
						{entry.courseName}
					</Typography>
				</Box>
			</Box>
			<DialogContent className={classes.content} classes={{ root: classes.contentRoot }}>
				<Typography variant='subtitle1' className={classes.subTitle} sx={{ marginTop: theme.spacing(2) }}>
					Module ID: {entry.moduleId ?? entry.module}
				</Typography>
				<Typography variant='subtitle1' className={classes.subTitle} sx={{ marginBottom: theme.spacing(2) }}>
					Entry ID: {entry.id}
				</Typography>
				<EntryContentDataView entry={entry} isMapView={isMapView} />
			</DialogContent>
			{!isMapView && <EntryContentButtonBoxView entryState={entry.state} entryId={entry.id} />}
		</>
	);
});
